<template>
  <div>
    <!-- b-select, size="sm", no red-active styling -->
    <b-select size="sm" :value="mode" :options="modeOptions" @change="onModeChange"></b-select>

    <div v-if="mode === 'specific_date'" class="mt-2">
      <label class="fsb-label">{{ $t('search.label.perimeterConfiguration.perimeterSingleDate') }}</label>
      <datepicker :value.sync="specificDate"></datepicker>
    </div>

    <!-- Stacked vertically, not side-by-side like RangeFilter's numeric from/to (.fsb-range-row is
         a flex row) — the two market-date pickers need their own full-width row each. -->
    <div v-if="mode === 'window'" class="mt-2">
      <div>
        <label class="fsb-label">{{ $t('search.label.perimeterConfiguration.perimeterRangeDateFrom') }}</label>
        <datepicker :value.sync="windowFrom"></datepicker>
      </div>
      <div class="mt-2">
        <label class="fsb-label">{{ $t('search.label.perimeterConfiguration.perimeterRangeDateTo') }}</label>
        <datepicker :value.sync="windowTo"></datepicker>
      </div>
    </div>

    <div v-if="!hideInPerimeter && (mode === 'specific_date' || mode === 'window')" class="mt-2">
      <label class="fsb-label">{{ $t('search.label.perimeterConfiguration.perimeterSdOptions') }}</label>
      <custom-multi-select
        :options="inPerimeterOptions"
        :value="inPerimeter ? [inPerimeter] : []"
        :default-text="$t('common.allMale')"
        :select-all-text="$t('common.selectAllMale')"
        maintain-original-order
        @input="onInPerimeterChange"
      ></custom-multi-select>
    </div>
  </div>
</template>

<script>
import CustomMultiSelect from '../../Lib/CustomMultiSelect'
import Datepicker from '../../Lib/DatePicker'

// Composite Perimeter filter: one filter_key, one value with a mode discriminant. Modes
// and in_perimeter codes come from the catalog row's value_schema — no hardcoded list. Widgets are
// b-select, Datepicker and CustomMultiSelect rather than native inputs.
const ALL_REVISIONS_CODE = 'all_revisions'

export default {
  name: 'perimeterFilter',
  components: { CustomMultiSelect, Datepicker },
  props: {
    valueSchema: { type: Object, default: () => ({ modes: [], in_perimeter: [] }) },
    value: { type: Object, default: null },
    // Scoring profiles: when the catalog restricts `modes` to only "specific_date" (no
    // "most_recent" entry), the b-select has no option matching the default `mode` of
    // 'most_recent' and would render blank. Callers that need a placeholder in that empty state
    // (e.g. ScoringProfileFiltersPanel) pass this prop; search leaves it unset (null),
    // since "most_recent" is always a real option there.
    placeholder: { type: String, default: null },
    // The scoring-profile page never surfaces the "prodotti in perimetro" sub-select (only the
    // search page does), even though the BE still models/persists an in_perimeter value (fixed to
    // "active_only" for scoring profiles). ScoringProfileFiltersPanel sets this to hide the control;
    // search leaves it unset (false) and keeps showing it.
    hideInPerimeter: { type: Boolean, default: false },
  },
  computed: {
    mode() {
      return this.value ? this.value.mode : 'most_recent'
    },
    inPerimeter() {
      return this.value ? this.value.in_perimeter : null
    },
    modeOptions() {
      const options = (this.valueSchema.modes || []).map((code) => ({ value: code, text: this.modeLabel(code) }))
      const hasCurrentMode = options.some((option) => option.value === this.mode)
      if (this.placeholder && !hasCurrentMode) {
        return [{ value: this.mode, text: this.placeholder, disabled: true }, ...options]
      }
      return options
    },
    // "all_revisions" is offered only to users holding the KPI-export-all-revisions permission (id 9),
    // the same one the BE enforces on search and export.
    inPerimeterOptions() {
      return (this.valueSchema.in_perimeter || [])
        .filter((code) => code !== ALL_REVISIONS_CODE || this.canAllRevisions)
        .map((code) => ({ value: code, text: this.inPerimeterLabel(code) }))
    },
    canAllRevisions() {
      return !!this.$store.getters.can_ExportAllRevisions
    },
    specificDate: {
      get() {
        return this.value && this.value.date ? new Date(this.value.date) : null
      },
      set(newDate) {
        this.emit({ mode: 'specific_date', date: toIsoDate(newDate), in_perimeter: this.inPerimeter })
      },
    },
    windowFrom: {
      get() {
        return this.value && this.value.from ? new Date(this.value.from) : null
      },
      set(newDate) {
        this.emit({ mode: 'window', from: toIsoDate(newDate), to: this.value && this.value.to, in_perimeter: this.inPerimeter })
      },
    },
    windowTo: {
      get() {
        return this.value && this.value.to ? new Date(this.value.to) : null
      },
      set(newDate) {
        this.emit({ mode: 'window', from: this.value && this.value.from, to: toIsoDate(newDate), in_perimeter: this.inPerimeter })
      },
    },
  },
  methods: {
    modeLabel(code) {
      const key = 'search.perimeterModes.' + code
      return this.$te(key) ? this.$t(key) : code
    },
    inPerimeterLabel(code) {
      const key = 'search.perimeterInPerimeter.' + code
      return this.$te(key) ? this.$t(key) : code
    },
    emit(value) {
      this.$emit('input', value)
    },
    onModeChange(newMode) {
      if (newMode === 'most_recent') {
        this.emit(null)
        return
      }
      // Switching to "specific_date" seeds today's date + "active_only", not an empty/undefined perimeter.
      if (newMode === 'specific_date') {
        this.emit({ mode: newMode, date: toIsoDate(new Date()), in_perimeter: 'active_only' })
        return
      }
      // "window" seeds from = today minus 1 month, to = today, not two empty datepickers.
      if (newMode === 'window') {
        const today = new Date()
        const from = new Date(today)
        from.setMonth(from.getMonth() - 1)
        this.emit({ mode: newMode, from: toIsoDate(from), to: toIsoDate(today), in_perimeter: 'active_only' })
        return
      }
      this.emit({ mode: newMode })
    },
    onInPerimeterChange(selected) {
      this.emit({ ...this.value, in_perimeter: selected[0] || null })
    },
  },
}

function toIsoDate(date) {
  if (!date) return null
  return date.toISOString().slice(0, 10)
}
</script>
